// src/lib/districtLabel.js — THE district renderer. One implementation, four surfaces.
// ----------------------------------------------------------------------------
// A district is a PATH, not a code. `labels.js` states the governing rule — "no
// normalized token ever reaches the screen" — and district codes were the last
// thing violating it: a real client was sent a share link reading
// "TBS_VEDZISI, Tbilisi" (2026-07-26), and agents saw "· TBS_VEDZISI" in the bell.
//
// Input is the server's `district_path` (lib/core_districts.js), ordered
// OUTERMOST FIRST. Do not re-sort it — the client cannot re-derive the hierarchy
// and has no district.parent_code to walk.
//
// ⚠️ NOT in labels.js on purpose. labels.js imports i18n.js (the full translation
// table); ShareApp is a separate ~6.6 kB public bundle and must not pull that in.
// Hence: zero imports, and the caller passes `lang` rather than this module
// reading it. That keeps the one renderer usable from both bundles.
//
// ⚠️ There is deliberately NO district_code fallback. If the path is missing the
// caller renders nothing — no location beats an internal identifier on screen.
// Callers wanting a fallback should supply a real one (address, city), not a code.

// path: [{ code, name_ka, name_en }] outermost first → "საბურთალო, ვეძისი"
// lang: 'ka' | 'en' — which name wins when both exist (the other is the fallback,
//   so a district named in only one language still renders).
export function districtLabel(path, lang = 'en') {
  if (!Array.isArray(path) || path.length === 0) return ''
  const pick = lang === 'ka'
    ? d => d?.name_ka || d?.name_en
    : d => d?.name_en || d?.name_ka
  return path.map(pick).filter(Boolean).join(', ')
}

// The most specific level alone ("ვეძისი"). Use ONLY where the containing
// district is already on screen — a bare sub-district otherwise hides which
// district it belongs to, which is the whole reason 0208 stores the specific
// code and recovers the parent at read time.
export function districtLeaf(path, lang = 'en') {
  if (!Array.isArray(path) || path.length === 0) return ''
  return districtLabel(path.slice(-1), lang)
}

// ── DISPLAY-ONLY SHORT NAMES FOR QUARTERS AND MICRODISTRICTS ─────────────────────────────
// Owner, 2026-08-19, on a phone screenshot of the district and street facets:
//   *"We should also rename it from Long name to ვაჟას I კვ."* … *"I don't mean to rename
//   whole settlement. I mean to change name display in search as search bar's width don't
//   allow full name. On hover it still shows full canonical name."*
//
// So this is a RENDERER, not a rename. The gazetteer keeps "ვაჟა-ფშაველას გამზირის VI
// კვარტალი"; the 240px facet row shows "ვაჟას VI კვ." and `title` still carries the full
// canonical string, exactly as the 2026-08-18 hover fix established. Nothing downstream ever
// sees the short form — not the filter, not the chip row, not the URL.
//
// ⚠️ IT ONLY TOUCHES LABELS THAT NAME A NUMBERED UNIT (კვარტალი / მიკრორაიონი, plus the
// pre-abbreviated `კვ.` and `მ/რ` the gazetteer already contains in places). That gate is
// what keeps it off real streets: the corpus holds "ვაჟა-ფშაველას გამზირი", "ვაჟა-ფშაველას
// ქუჩა", "ვაჟა ფშაველას I შესახვევი" and ~30 more, and an ungated ვაჟა-ფშაველა→ვაჟას rule
// would have quietly renamed every one of them. Measured over the live corpus before
// shipping: 80 street rows and 77 district rows carry a numbered unit; every other name in
// both tables is returned untouched.
//
// Three transforms, in order:
//   1. SPLIT on the separators the gazetteer actually uses (` - `, `, `, ` / `) and put the
//      non-unit segment FIRST. The registry is inconsistent about which end the place goes
//      on — "ვაჟა-ფშაველა - I კვარტალი" but "II კვარტალი - ვაჟა ფშაველა", both live — and a
//      list that reads place-first in one row and number-first in the next cannot be scanned.
//   2. ABBREVIATE the unit words. These are the four the registry uses.
//   3. KEEP THE ORIGINAL unless the result is genuinely shorter. A transform that returns
//      something longer has misread the name, and the honest answer there is to do nothing.
//
// ⚠️ Known and NOT introduced here: `IV კვარტალი - დიღმის მასივი` (324 properties) and
// `დიღმის მასივი - IV კვარტალი` (20) fold to the same short label — because they are the
// same place recorded twice in `street`. The shortener surfaces that duplicate, it does not
// create it; the counts and the hover text still tell the two rows apart. Filed as a data
// row, not patched here.
const NUMBERED_UNIT = /მიკრო\s*რაიონ|მიკრორაიონ|კვარტალ|კვ\.|მ\/რ/
const UNIT_ABBR = [
  [/მიკრო\s*რაიონის|მიკრო\s*რაიონი|მიკრორაიონის|მიკრორაიონი/g, 'მკრ.'],
  [/კვარტალის|კვარტალი/g, 'კვ.'],
  [/მასივის|მასივი/g, 'მას.'],
  [/დასახლების|დასახლება/g, 'დას.'],
  // The one place name long enough to need its own rule, and the one the owner named.
  [/ვაჟა[-\s–]ფშაველას?(\s+გამზირის)?/g, 'ვაჟას'],
  // Owner, 2026-08-22, on the same facet: "ნუცუბიძის I მ/რ". Same shape as the
  // ვაჟას rule above — the parent row already reads "ნუცუბიძის პლატო" directly
  // above its children, so repeating "პლატოს" in every child buys nothing and
  // cost 7 characters of a 240px row.
  [/ნუცუბიძის\s+პლატოს/g, 'ნუცუბიძის'],
]

// Georgian ordinal → Roman, for numbered units only (this runs inside the
// NUMBERED_UNIT gate, so plain names like "ვაზისუბანი, მე-8 ლეგიონი" never reach it).
// The corpus is already inconsistent: OSM's Tbilisi microdistricts arrive as
// "მე-3", while the quarters the owner shortened in August arrive as "III". One
// facet showing both spellings of the same idea cannot be scanned, so normalise
// to the Roman form the existing short labels already use.
// A trailing Georgian letter is part of the number ("მე-10ა", "მე-4ბ") and is kept.
const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X',
               'XI', 'XII', 'XIII', 'XIV', 'XV', 'XVI', 'XVII', 'XVIII', 'XIX', 'XX', 'XXI']
// ⚠️ NO \b HERE. JS word boundaries are ASCII-only, so `\bმე-` can never match —
// a Georgian letter is not \w, so there is no boundary to find. The first cut of
// this function used \b and silently transformed nothing; 62 rows went through
// unchanged and the tests still passed, because every assertion happened to use
// names that were already Roman.
function romanise(s) {
  return s
    .replace(/მე-(\d+)([ა-ჰ]?)/g, (m, n, suf) => ROMAN[+n] ? ROMAN[+n] + suf : m)
    .replace(/(\d+)-ლი/g, (m, n) => ROMAN[+n] || m)
}
export function shortPlaceLabel(raw) {
  const full = String(raw || '').trim()
  if (!full || !NUMBERED_UNIT.test(full)) return full
  const parts = full.split(/\s+[-–—/]\s+|\s*,\s*/).map(s => s.trim()).filter(Boolean)
  let out = [...parts.filter(p => !NUMBERED_UNIT.test(p)),
             ...parts.filter(p => NUMBERED_UNIT.test(p))].join(' ')
  for (const [re, to] of UNIT_ABBR) out = out.replace(re, to)
  out = romanise(out)
  out = out.replace(/\s+/g, ' ').trim()
  return out && out.length < full.length ? out : full
}

